import React, { useRef, useState, useEffect, useCallback } from "react";
import Webcam from "react-webcam";
import axios from "axios";
import { useParams } from "react-router-dom";
import { showToast, WEBSERVICE } from "../../CoreDataService";
import { useSelector } from "react-redux";

const WIDTH = 640;
const HEIGHT = 480;

const videoConstraints = {
  width: WIDTH,
  height: HEIGHT,
  facingMode: "user",
};

const CapturePhoto = () => {
  const { btncolor } = useSelector((state) => state);
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [captures, setCaptures] = useState([]);
  const [isCaptured, setIsCaptured] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);
  const [failMsg, setFailMsg] = useState(false);
  const [error, setError] = useState(null);

  const { p1, q1 } = useParams();
  const [userUuid] = useState(p1 ? atob(p1) : "");
  const [userToken] = useState(q1 ? atob(q1) : "");
  const [faceDetected, setFaceDetected] = useState(true);
  const [lastDetectedTime, setLastDetectedTime] = useState(Date.now());

  const loadModels = async () => {
    const MODEL_URL = "/models";
    await window.faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
    await window.faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
  };

  const detectFace = useCallback(async () => {
    if (webcamRef.current && webcamRef.current.video.readyState === 4) {
      const video = webcamRef.current.video;
      const detections = await window.faceapi
        .detectAllFaces(video, new window.faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks();

      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const displaySize = {
          width: video.videoWidth,
          height: video.videoHeight,
        };
        window.faceapi.matchDimensions(canvas, displaySize);
        const resizedDetections = window.faceapi.resizeResults(
          detections,
          displaySize
        );
        canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
        window.faceapi.draw.drawDetections(canvas, resizedDetections);
        window.faceapi.draw.drawFaceLandmarks(canvas, resizedDetections);
      }

      if (detections.length > 0) {
        setFaceDetected(true);
        setLastDetectedTime(Date.now());
      } else {
        const timeSinceLastDetection = Date.now() - lastDetectedTime;
        if (timeSinceLastDetection > 2000) {
          // 2 seconds threshold
          setFaceDetected(false);
        }
      }
    }
  }, [lastDetectedTime]);

  useEffect(() => {
    loadModels();
    const interval = setInterval(detectFace, 300); // every 300ms
    return () => clearInterval(interval);
  }, [detectFace]);

  const capture = () => {
    const screenshot = webcamRef.current.getScreenshot();
    if (screenshot) {
      setCaptures([screenshot]);
      setIsCaptured(true);

      const img = new Image();
      img.src = screenshot;
      img.onload = () => {
        const ctx = canvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, WIDTH, HEIGHT);
        ctx.drawImage(img, 0, 0, WIDTH, HEIGHT);
      };
    }
  };

  const removeCurrent = () => {
    setIsCaptured(false);
    setCaptures([]);
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  };

  const submitPhoto = async () => {
    try {
      const userPhotoBlob = dataURItoBlob(captures[0]);
      const formData = new FormData();
      formData.append("token", userToken);
      formData.append("uuid", userUuid);
      formData.append("userPhoto", userPhotoBlob, "userPhoto.png");

      const response = await axios.post(
        `${WEBSERVICE}user/uploadUserPhoto`,
        formData,
        {
          headers: {
            Authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      if (response.data.error?.error_data !== 0) {
        showToast("error", response.data.error?.error_msg);
        setFailMsg(true);
        setSuccessMsg(false);
      } else {
        setSuccessMsg(true);
        setFailMsg(false);
      }
    } catch (err) {
      setFailMsg(true);
      setSuccessMsg(false);
      setError(err.message || "Upload failed");
    }
  };

  return (
    <div className="content-wrapper pt-0 pl-3 Dark">
      <div className="container-fluid">
        <div className="row d-flex">
          <div className="col-12 mt-3">
            {!successMsg && (
              <div className="p-2 pr-3 rounded align-self-stretch h-100 Dark">
                <div className="d-flex justify-content-center">
                  <h1>Capture Photo</h1>
                </div>

                <div className="d-flex justify-content-center video-container position-relative">
                  {!isCaptured ? (
                    <div style={{ position: "relative" }}>
                      <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/png"
                        videoConstraints={{ facingMode: "user" }}
                        style={{ width: "100%" }}
                      />
                      <canvas
                        ref={canvasRef}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "4 / 3",
                      }}
                    >
                      <img
                        src={captures}
                        alt="Captured"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                    </div>
                  )}
                  {!faceDetected && (
                    <div
                      style={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        backgroundColor: "rgba(255, 0, 0, 0.8)",
                        color: "#fff",
                        padding: "10px",
                        borderRadius: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      No face detected. Please adjust your position.
                    </div>
                  )}
                  {isCaptured && (
                    <canvas
                      ref={canvasRef}
                      width={WIDTH}
                      height={HEIGHT}
                      className="rounded border"
                    />
                  )}
                </div>

                <div className="snap-container mt-3 text-center">
                  {!isCaptured && faceDetected && (
                    <button
                      className="btn btn-primary"
                      style={{ backgroundColor: btncolor, color: "#fff" }}
                      onClick={capture}
                    >
                      Snap Photo
                    </button>
                  )}
                  {isCaptured && (
                    <>
                      <button
                        className="btn btn-danger"
                        style={{ backgroundColor: btncolor, color: "#fff" }}
                        onClick={removeCurrent}
                      >
                        Take Another
                      </button>
                      <button
                        className="btn btn-primary ml-4"
                        style={{ backgroundColor: btncolor, color: "#fff" }}
                        onClick={submitPhoto}
                      >
                        Submit
                      </button>
                    </>
                  )}
                  {error && (
                    <div className="text-danger mt-4">
                      {error}. Please refresh the page.
                    </div>
                  )}
                </div>
              </div>
            )}

            {successMsg && (
              <div className="d-flex justify-content-center mt-3">
                <h4 className="text-success">
                  Your photo was uploaded successfully
                </h4>
              </div>
            )}
            {failMsg && (
              <div className="d-flex justify-content-center mt-3">
                <h4 className="text-danger">
                  Oops! Your photo was not uploaded. Please try again.
                </h4>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapturePhoto;
